import { useState, useEffect, useMemo } from "react";
import {
  Search, X, Calendar, ArrowRight, ChevronRight,
  Bookmark, LayoutGrid, List, FileText, Download, HardDrive,
} from "lucide-react";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils.js";
import { useLanguage } from "../../context/LanguageContext.jsx";

const CATEGORIES = ["all", "report", "logo", "news", "form", "policy"];

const categoryConfig = {
  ne: {
    report: { label: "प्रतिवेदन",      dot: "#15803d", bg: "rgba(21,128,61,0.12)",  text: "#15803d" },
    logo:   { label: "लोगो / ब्रान्ड", dot: "#f97316", bg: "rgba(249,115,22,0.12)", text: "#ea580c" },
    news:   { label: "समाचार कागजात", dot: "#6366f1", bg: "rgba(99,102,241,0.12)", text: "#4f46e5" },
    form:   { label: "फारमहरू",        dot: "#0ea5e9", bg: "rgba(14,165,233,0.12)", text: "#0284c7" },
    policy: { label: "नीति",           dot: "#6b7280", bg: "#f3f4f6",               text: "#4b5563" },
  },
  en: {
    report: { label: "Report",         dot: "#15803d", bg: "rgba(21,128,61,0.12)",  text: "#15803d" },
    logo:   { label: "Logo / Brand",   dot: "#f97316", bg: "rgba(249,115,22,0.12)", text: "#ea580c" },
    news:   { label: "News Document",  dot: "#6366f1", bg: "rgba(99,102,241,0.12)", text: "#4f46e5" },
    form:   { label: "Forms",          dot: "#0ea5e9", bg: "rgba(14,165,233,0.12)", text: "#0284c7" },
    policy: { label: "Policy",         dot: "#6b7280", bg: "#f3f4f6",               text: "#4b5563" },
  },
};

const CONTENT = {
  ne: {
    eyebrow:           "Resources",
    pageTitle:         "डाउनलोडहरू",
    pageTitleAccent:   "& कागजातहरू",
    pageDesc:          "प्रतिवेदन, फारम, लोगो र नीति दस्तावेजहरू — भोलिको इटहरीका आधिकारिक स्रोतहरू।",
    statTotal:         "कुल फाइल",
    statReport:        "प्रतिवेदन",
    statForm:          "फारम",
    statPolicy:        "नीति",
    downloadBtn:       "डाउनलोड गर्नुहोस्",
    downloadShort:     "डाउनलोड",
    searchPlaceholder: "कागजात खोज्नुहोस्…",
    filterAll:         "सबै",
    docCount:          (n) => `${n} कागजात`,
    loading:           "डाउनलोड लोड हुँदैछ…",
    emptyTitle:        "कुनै कागजात फेला परेन",
    emptyDesc:         "खोज वा श्रेणी परिवर्तन गर्नुहोस्",
    resetFilter:       "फिल्टर रिसेट गर्नुहोस्",
    infoBanner:        (email) => (
      <>
        डाउनलोड नभएमा ब्राउजर सेटिङ जाँच गर्नुहोस् वा{" "}
        <a href={`mailto:${email}`} className="font-semibold underline">{email}</a>{" "}
        मा सम्पर्क गर्नुहोस्।
      </>
    ),
  },
  en: {
    eyebrow:           "Resources",
    pageTitle:         "Downloads",
    pageTitleAccent:   "& Documents",
    pageDesc:          "Reports, forms, logos, and policy documents — official resources from Bholiko Itahari.",
    statTotal:         "Total Files",
    statReport:        "Reports",
    statForm:          "Forms",
    statPolicy:        "Policies",
    downloadBtn:       "Download",
    downloadShort:     "Download",
    searchPlaceholder: "Search documents…",
    filterAll:         "All",
    docCount:          (n) => `${n} document${n !== 1 ? "s" : ""}`,
    loading:           "Loading downloads…",
    emptyTitle:        "No documents found",
    emptyDesc:         "Try changing your search or category",
    resetFilter:       "Reset Filters",
    infoBanner:        (email) => (
      <>
        If a download fails, check your browser settings or contact us at{" "}
        <a href={`mailto:${email}`} className="font-semibold underline">{email}</a>.
      </>
    ),
  },
};



const CONTACT_EMAIL = "info@bholikoitahari.org";

function fmtDate(d, language) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(language === "ne" ? "ne-NP" : "en-US", {
    day: "numeric", month: "short", year: "numeric",
  });
}

const API_BASE = (import.meta.env.VITE_API_URL || "http://localhost:8001").replace(/\/api\/?$/, "");

function fileUrl(item) {
  const url = item.fileUrl || item.downloadUrl || "#";
  if (url === "#" || /^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}

function thumbnailSrc(item) {
  const url = item.thumbnailUrl;
  if (!url) return null;
  return getImageUrl(url);
} 

export default function DownloadsPage() {
  const { language } = useLanguage();
  const t   = CONTENT[language];
  const cfg = categoryConfig[language];

  const categoryLabel = (id) => id === "all" ? t.filterAll : (cfg[id]?.label || id);

  const [downloads,    setDownloads]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search,       setSearch]       = useState("");
  const [view,         setView]         = useState("grid");
  const [saved,        setSaved]        = useState([]);

  useEffect(() => { fetchDownloads(); }, []);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const res  = await api.get("/downloads");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setDownloads(data);
    } catch (err) {
      console.error("Failed to load downloads:", err);
      setDownloads([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSave = (id) =>
    setSaved((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const filtered = useMemo(() =>
    downloads.filter((d) => {
      const q = search.toLowerCase();
      return (
        (d.title?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q)) &&
        (activeFilter === "all" || d.category === activeFilter)
      );
    }),
    [downloads, search, activeFilter]
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--background)", fontFamily: "'Inter', sans-serif" }}>

      {/* ── Page Header ── */}
      <div style={{ background: "rgb(20, 83, 45)" }} className="pt-20 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <p className="text-green-300/80 text-xs tracking-widest uppercase font-semibold mb-3">{t.eyebrow}</p>
              <h1 className="text-white mb-3"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem,4vw,3.2rem)", fontWeight: 700, lineHeight: 1.1 }}>
                {t.pageTitle}
                <br /><span style={{ color: "rgb(249 115 22 / 80%)" }}>{t.pageTitleAccent}</span>
              </h1>
              <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.9rem", lineHeight: 1.7 }}>
                {t.pageDesc}
              </p>
            </div>

            <div className="flex gap-3 flex-wrap">
              {[
                { label: t.statTotal,  value: String(downloads.length) },
                { label: t.statReport, value: String(downloads.filter((d) => d.category === "report").length) },
                { label: t.statForm,   value: String(downloads.filter((d) => d.category === "form").length) },
                { label: t.statPolicy, value: String(downloads.filter((d) => d.category === "policy").length) },
              ].map((s) => (
                <div key={s.label} className="rounded-xl px-4 py-3 border text-center min-w-[72px]"
                  style={{ background: "rgb(21, 75, 43)", borderColor: "rgba(255,255,255,0.1)" }}>
                  <p className="text-white font-bold" style={{ fontSize: "1.4rem", lineHeight: 1 }}>{s.value}</p>
                  <p style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.35)", marginTop: "3px" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Search + Filter Bar ── */}
      <div className="sticky top-0 z-30 border-b"
        style={{ background: "rgba(248,249,252,0.96)", backdropFilter: "blur(12px)", borderColor: "#eef0f3" }}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 flex-wrap">

            <div className="relative flex-1 min-w-48 max-w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "var(--muted-foreground)" }} />
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-2 rounded-xl border text-sm outline-none transition-all"
                style={{ background: "white", borderColor: "#10101066", color: "var(--foreground)", fontSize: "0.83rem" }}
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                </button>
              )}
            </div>

            <div className="flex gap-1.5 flex-wrap">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setActiveFilter(c)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{
                    background:  activeFilter === c ? "rgb(20 83 45)" : "white",
                    color:       activeFilter === c ? "white" : "var(--muted-foreground)",
                    border:      activeFilter === c ? "1px solid rgb(20 83 45)" : "1px solid #eef0f3",
                  }}>
                  {categoryLabel(c)}
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <p className="text-xs hidden sm:block" style={{ color: "var(--muted-foreground)" }}>
                {t.docCount(filtered.length)}
              </p>
              <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: "#eef0f3" }}>
                {["grid", "list"].map((v) => {
                  const Icon = v === "grid" ? LayoutGrid : List;
                  return (
                    <button key={v} onClick={() => setView(v)}
                      className="p-1.5 transition-colors"
                      style={{
                        background: view === v ? "var(--secondary)" : "white",
                        color:      view === v ? "rgb(20 83 45)" : "var(--muted-foreground)",
                      }}>
                      <Icon className="w-3.5 h-3.5" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Grid / List ── */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <div className="text-center py-24">
            <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>{t.loading}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <p style={{ fontSize: "2.5rem" }}>📭</p>
            <p className="mt-3 font-semibold" style={{ color: "var(--foreground)" }}>{t.emptyTitle}</p>
            <p className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>{t.emptyDesc}</p>
            <button
              onClick={() => { setSearch(""); setActiveFilter("all"); }}
              className="mt-4 px-4 py-2 rounded-lg text-sm"
              style={{ background: "var(--secondary)", color: "rgb(20 83 45)" }}>
              {t.resetFilter}
            </button>
          </div>
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((item) => (
              <GridCard
                key={item._id || item.id}
                item={item}
                saved={saved.includes(item._id || item.id)}
                onSave={() => toggleSave(item._id || item.id)}
                cfg={cfg}
                language={language}
                downloadBtn={t.downloadBtn}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filtered.map((item) => (
              <ListCard
                key={item._id || item.id}
                item={item}
                saved={saved.includes(item._id || item.id)}
                onSave={() => toggleSave(item._id || item.id)}
                cfg={cfg}
                language={language}
                downloadShort={t.downloadShort}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

/* ── Grid Card ── */
function GridCard({ item, saved, onSave, cfg, language, downloadBtn }) {
  const [hov, setHov] = useState(false);
  const c = cfg[item.category] || cfg.report;

  return (
    <div
      className="rounded-2xl border overflow-hidden flex flex-col transition-all duration-250"
      style={{
        background:  "var(--card)",
        borderColor: hov ? "rgba(20,83,45,0.3)" : "#eef0f3",
        boxShadow:   hov ? "0 12px 32px rgba(0,0,0,0.1)" : "0 1px 4px rgba(0,0,0,0.04)",
        transform:   hov ? "translateY(-4px)" : "none",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}>

      <div className="relative h-44 overflow-hidden flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #14532d 0%, #166534 100%)" }}>
        {thumbnailSrc(item) ? (
          <img src={thumbnailSrc(item)} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <FileText className="w-16 h-16 text-white/25" />
        )}
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.25) 100%)" }} />

        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: c.bg, border: `1px solid ${c.dot}40`, color: c.text }}>
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: c.dot }} />
            {c.label}
          </span>
        </div>

        <div className="absolute top-3 right-3 flex gap-2">
          <span className="px-2 py-1 rounded-md text-xs font-bold text-white"
            style={{ background: "rgba(0,0,0,0.35)" }}>
            {item.fileType}
          </span>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold leading-snug mb-2 flex-1"
          style={{ fontSize: "0.9rem", color: "var(--foreground)" }}>
          {item.title}
        </h3>
        <p className="mb-3 leading-relaxed line-clamp-2"
          style={{ fontSize: "0.74rem", color: "var(--muted-foreground)", lineHeight: 1.6 }}>
          {item.description}
        </p>

        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2" style={{ fontSize: "0.73rem", color: "var(--muted-foreground)" }}>
            <Calendar className="w-3.5 h-3.5 shrink-0" style={{ color: "rgb(20 83 45)" }} />
            {fmtDate(item.createdAt, language)}
          </div>
          <div className="flex items-center gap-2" style={{ fontSize: "0.73rem", color: "var(--muted-foreground)" }}>
            <HardDrive className="w-3.5 h-3.5 shrink-0" style={{ color: "rgb(20 83 45)" }} />
            {item.fileSize || "—"}
          </div>
        </div>

        <a
          href={fileUrl(item)}
          download
          className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all no-underline"
          style={{
            background: hov ? "rgb(20 83 45)" : "var(--secondary)",
            color:      hov ? "white" : "rgb(20 83 45)",
          }}>
          {downloadBtn}
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

/* ── List Card ── */
function ListCard({ item, saved, onSave, cfg, language, downloadShort }) {
  const [hov, setHov] = useState(false);
  const c = cfg[item.category] || cfg.report;

  return (
    <div
      className="rounded-2xl border flex gap-0 overflow-hidden transition-all duration-200"
      style={{
        background:  "var(--card)",
        borderColor: hov ? "rgba(20,83,45,0.3)" : "#eef0f3",
        boxShadow:   hov ? "0 6px 20px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.04)",
        transform:   hov ? "translateY(-2px)" : "none",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}>

      <div className="w-1 shrink-0 transition-colors"
        style={{ background: hov ? "rgb(20 83 45)" : "transparent" }} />

      <div className="hidden sm:flex w-36 shrink-0 items-center justify-center overflow-hidden"
        style={{ background: "linear-gradient(135deg, #14532d 0%, #166534 100%)" }}>
        {thumbnailSrc(item) ? (
          <img src={thumbnailSrc(item)} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <FileText className="w-10 h-10 text-white/30" />
        )}
      </div>

      <div className="flex-1 p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ background: c.bg, border: `1px solid ${c.dot}40`, color: c.text }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: c.dot }} />
              {c.label}
            </span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-md"
              style={{ background: "#f3f4f6", color: "#374151" }}>
              {item.fileType}
            </span>
          </div>
          <h3 className="font-semibold mb-1 leading-snug"
            style={{ fontSize: "0.92rem", color: "var(--foreground)" }}>
            {item.title}
          </h3>
          <p className="line-clamp-1 mb-2"
            style={{ fontSize: "0.75rem", color: "var(--muted-foreground)" }}>
            {item.description}
          </p>
          <div className="flex flex-wrap gap-4">
            {[
              { Icon: Calendar,  text: fmtDate(item.createdAt, language) },
              { Icon: HardDrive, text: item.fileSize || "—" },
            ].map(({ Icon, text }) => (
              <span key={text} className="flex items-center gap-1.5"
                style={{ fontSize: "0.72rem", color: "var(--muted-foreground)" }}>
                <Icon className="w-3 h-3" style={{ color: "rgb(20 83 45)" }} />{text}
              </span>
            ))}
          </div>
        </div>

        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 shrink-0">
          <a
            href={fileUrl(item)}
            download
            className="flex items-center gap-1 px-4 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap no-underline"
            style={{
              background: hov ? "rgb(20 83 45)" : "var(--secondary)",
              color:      hov ? "white" : "rgb(20 83 45)",
            }}>
            {downloadShort}
            <ChevronRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}